import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Typography,
  Badge,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconShield, IconSettings } from "@tabler/icons-react";
import type { Role, Setting, RoleSetting } from "../../services/rbac";

interface SettingsTabProps {
  roles: Role[];
  settings: Setting[];
  roleSettings: RoleSetting[];
}

export const SettingsTab = ({ roles, settings, roleSettings }: SettingsTabProps) => {
  return (
    <div className="al-space-y-6 al-mt-6">
      <Card>
        <CardHeader>
          <Stack direction="row" align="center" gap="sm">
            <IconShield className="al-w-5 al-h-5 al-text-blue-600" />
            <CardTitle>Roles</CardTitle>
          </Stack>
          <CardDescription>Roles granted to policy_admin user</CardDescription>
        </CardHeader>
        <CardContent>
          {roles.length === 0 ? (
            <Typography variant="body" className="al-text-gray-500">
              No roles assigned
            </Typography>
          ) : (
            <div className="al-space-y-3">
              {roles.map((role, index: number) => (
                <div
                  key={index}
                  className="al-p-4 al-bg-blue-50 al-border al-border-blue-200 al-rounded-lg"
                >
                  <Stack direction="row" justify="between" align="center">
                    <Stack direction="column" gap="xs">
                      <Typography variant="section-heading">{role.role_name}</Typography>
                      <Stack direction="row" gap="sm">
                        {role.is_default && (
                          <Badge className="!al-bg-green-100 !al-text-green-700 al-border al-border-green-200">
                            Default
                          </Badge>
                        )}
                        {role.with_admin_option && (
                          <Badge className="!al-bg-purple-100 !al-text-purple-700 al-border al-border-purple-200">
                            Admin Option
                          </Badge>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <Stack direction="row" align="center" gap="sm">
            <IconSettings className="al-w-5 al-h-5 al-text-blue-600" />
            <CardTitle>Settings Profile</CardTitle>
          </Stack>
          <CardDescription>Custom settings for playground_settings profile</CardDescription>
        </CardHeader>
        <CardContent>
          {settings.length === 0 ? (
            <Typography variant="body" className="al-text-gray-500">
              No settings configured
            </Typography>
          ) : (
            <div className="al-space-y-2">
              {settings.map((setting, index: number) => (
                <div
                  key={index}
                  className="al-grid al-grid-cols-2 al-gap-4 al-p-3 al-bg-gray-50 al-border al-border-gray-200 al-rounded-lg"
                >
                  <Typography variant="body" className="al-font-medium">
                    {setting.setting_name}
                  </Typography>
                  <Typography variant="body" className="al-font-mono">
                    {setting.value}
                  </Typography>
                </div>
              ))}
            </div>
          )}

          {roleSettings.length > 0 && (
            <div className="al-mt-6">
              <Typography variant="section-heading" className="al-mb-3 al-text-gray-700">
                Settings Profile Assignment
              </Typography>
              <div className="al-space-y-2">
                {roleSettings.map((rs, index: number) => (
                  <div
                    key={index}
                    className="al-p-3 al-bg-indigo-50 al-border al-border-indigo-200 al-rounded-lg"
                  >
                    <Typography variant="body">
                      <span className="al-font-medium">{rs.role_name}</span>
                      {" → "}
                      <span className="al-text-indigo-700">{rs.setting_profile_name}</span>
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
